import { memo, type ComponentType } from 'react';
import { Pressable, Text, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { coerceLocale, muscleName, type Exercise } from '@heraion/shared';

import { Icon } from '../Icon/Icon';
import { Badge } from '../primitives/Badge';
import { SelectMark } from '../primitives/SelectMark';
import type { UiTheme } from '../theme';
import type { PickerStyles } from './pickerStyles';
import { stateAria } from "../a11y";

// One exercise row in the unified picker. Tap = pick (single mode) or toggle
// selection (multi-select mode). The ⭐ on the right pins/unpins the favourite
// (its own tap target, doesn't trigger the row). The select dot shows only in
// multi-select mode. The pain-risk dot is host-injected (mobile passes its
// PainDot, which reads the profile's pain map; hosts without one pass nothing).
export const ExercisePickerRow = memo(function ExercisePickerRow({
  exercise,
  name,
  selected,
  multiSelect,
  bookmarked,
  onPress,
  onToggleStar,
  PainDot,
  usePainRiskLabel,
  theme,
  presentLabel,
  styles,
}: {
  exercise: Exercise;
  name: string;
  selected: boolean;
  multiSelect: boolean;
  bookmarked: boolean;
  onPress: () => void;
  onToggleStar: () => void;
  PainDot?: ComponentType<{ exercise: Exercise }>;
  /** What PainDot SAYS (see bodymap/PainDot.tsx) — the row's own
   *  accessibilityLabel collapses the subtree, so the dot's own label
   *  never reaches assistive tech unless folded in here explicitly
   *  (same fix ExerciseSwapPickerRow already applies, same host-injected
   *  hook shape as hostDeps.ts's own usePainRiskLabel slot). */
  usePainRiskLabel?: (exercise: Exercise) => string | null;
  theme: UiTheme;
  /** LG-41 — set when this exercise is already on the draft. The row stays
   *  tappable: adding it a second time is an intention already expressed. */
  presentLabel: string | null;
  styles: PickerStyles;
}) {
  const { t, i18n } = useTranslation();
  const muscle = exercise.primaryMuscle
    ? muscleName(exercise.primaryMuscle, coerceLocale(i18n.language))
    : null;
  // Host-injected hook, stable per mount (same exception as
  // ExerciseSwapPickerRow's identical slot — see hostDeps.ts).
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const painLabel = usePainRiskLabel ? usePainRiskLabel(exercise) : null;
  return (
    <Pressable
      accessibilityRole="button"
      // Spelled out instead of left to RN's descendant-text concatenation.
      // The row now carries a third fact (the LG-41 mark) and the implicit
      // name is whatever view-flattening happens to produce that build: two
      // "not started" rows of the same exercise would announce identically,
      // which is the DS-43 defect one screen over. The star below has always
      // named itself explicitly; the row now does too.
      accessibilityLabel={[name, painLabel, muscle, presentLabel]
        .filter((part): part is string => typeof part === 'string' && part !== '')
        .join(', ')}
      accessibilityState={{ selected: multiSelect ? selected : undefined }}
      {...stateAria({ selected: multiSelect ? selected : undefined }, "button")}
      style={({ pressed }) => [
        styles.row,
        selected && styles.rowSelected,
        pressed && styles.rowPressed,
      ]}
      onPress={onPress}
    >
      {multiSelect ? <SelectMark theme={theme} checked={selected} /> : null}
      <View style={styles.rowBody}>
        <View style={styles.rowNameLine}>
          {PainDot ? <PainDot exercise={exercise} /> : null}
          <Text style={styles.rowName} numberOfLines={1}>
            {name}
          </Text>
        </View>
        {muscle ? (
          <Text style={styles.rowSub} numberOfLines={1}>
            {muscle}
          </Text>
        ) : null}
        {presentLabel !== null ? (
          <View style={styles.rowMark}>
            <Badge theme={theme} tone="neutral" label={presentLabel} />
          </View>
        ) : null}
      </View>
      <Pressable
        accessibilityRole="button"
        accessibilityState={{ selected: bookmarked }}
        {...stateAria({ selected: bookmarked }, "button")}
        accessibilityLabel={t(
          bookmarked ? 'mobile.logger.pickerUnpin' : 'mobile.logger.pickerPin',
          { defaultValue: bookmarked ? 'Unpin favourite' : 'Pin favourite' },
        )}
        hitSlop={6}
        onPress={onToggleStar}
        style={styles.star}
      >
        <Icon
          name="star"
          size={styles.starText.fontSize ?? 20}
          color={bookmarked ? styles.starOn.color : styles.starOff.color}
          fill={bookmarked ? styles.starOn.color : 'none'}
        />
      </Pressable>
    </Pressable>
  );
});
